import { SimpleGrid, Stack, Text, Title } from '@repletoryreach/sdk-ui';
import { MetricCard } from '@repletoryreach/sdk-ui/data-display';
import { DonutChart, LineChart } from '@repletoryreach/sdk-ui/charts';
import { useI18n } from '../../../i18n/I18nProvider';
import { formatCount } from '../../../lib/format';
import { formatLocalDate } from '../../bookings/lib/time';
import { change, countBy, heat, kpis, perDay } from '../lib/data';
import { Bullets, MUTED, Muted, Panel, SeeChats, WholeReport, channelLabel, intlTag } from '../shared';
import { HeatGrid } from './HeatGrid';
import type { TabProps } from './tab';

/**
 * The report at a glance: the figures (with the change on the period before when nothing is filtered), what the AI
 * found, chats by day, when they start and on which channel, and the next steps.
 */
export function Overview({ report, rows, filters, filtered, tz, onSee }: TabProps) {
  const { t, locale } = useI18n();
  const k = kpis(rows);
  const prev = filtered ? null : report.metrics?.previous ?? null;
  const s = report.sections;
  const days = perDay(rows, filters.from, filters.to, tz);
  const channels = countBy(rows.map((r) => r.channel));
  const minutes = (m: number | null) => (m == null ? t('common.none') : m < 60 ? t('insights.minutes', { n: Math.round(m) }) : t('insights.hours', { n: Math.round(m / 6) / 10 }));
  /**
   * The change on the period before, in words under the figure. Never as the card's coloured trend: whether a rise is
   * good depends on the figure (more chats is good, more left unanswered is not), and the card colours every rise green.
   */
  const pctFmt = new Intl.NumberFormat(intlTag(locale), { maximumFractionDigits: 1, signDisplay: 'exceptZero' });
  const before = (now: number, then: number | null | undefined) => {
    const c = change(now, then);
    return c == null ? undefined : t('insights.kpi.before', { change: `${pctFmt.format(c)}%` });
  };

  return (
    <Stack gap="lg">
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md" data-testid="insights-kpis" data-guide="insights-kpis">
        <MetricCard label={t('insights.kpi.chats')} value={formatCount(k.chats)} hint={before(k.chats, prev?.chats)} data-testid="insights-kpi-chats" />
        <MetricCard label={t('insights.kpi.people')} value={formatCount(k.people)}
          hint={t('insights.kpi.newPeople', { count: formatCount(k.newPeople) })} />
        <MetricCard label={t('insights.kpi.answeredByBot')} value={k.answeredByBotPct == null ? t('common.none') : `${k.answeredByBotPct}%`}
          hint={t('insights.kpi.answeredByBotHint', { count: formatCount(k.read) })} data-testid="insights-kpi-answered" />
        <MetricCard label={t('insights.kpi.handedToTeam')} value={formatCount(k.handedToTeam)} hint={before(k.handedToTeam, prev?.taken_over)} />
        <MetricCard label={t('insights.kpi.unanswered')} value={formatCount(k.unanswered)}
          hint={before(k.unanswered, prev?.unanswered) ?? t('insights.kpi.unansweredHint')} data-testid="insights-kpi-unanswered" />
        <MetricCard label={t('insights.kpi.firstReply')} value={minutes(k.firstReplyMedian)} hint={t('insights.kpi.firstReplyHint')} />
        <MetricCard label={t('insights.kpi.buying')} value={formatCount(k.buyingStrong + k.buyingSome)}
          hint={t('insights.kpi.buyingHint', { count: formatCount(k.buyingStrong) })} />
        <MetricCard label={t('insights.kpi.read')} value={formatCount(k.read)}
          hint={report.sampled ? t('insights.kpi.sampled') : t('insights.kpi.readHint')} />
      </SimpleGrid>
      {filtered && <Muted>{t('insights.kpi.filtered')}</Muted>}

      {s && (
        <Panel title={s.headline || t('insights.overview.summary')} note={<WholeReport t={t} />} testId="insights-summary">
          <Stack gap="xs">
            {s.summary.map((p, i) => <Text key={i} role="bodySmall">{p}</Text>)}
          </Stack>
          {s.highlights.length > 0 && (
            <Stack gap={4}>
              <Title order={5}>{t('insights.overview.highlights')}</Title>
              <Bullets items={s.highlights} />
            </Stack>
          )}
        </Panel>
      )}

      <Panel title={t('insights.overview.perDay')} note={t('insights.overview.perDayNote')}>
        {rows.length === 0 ? <Muted>{t('insights.empty.filtered')}</Muted> : (
          <LineChart data={days as unknown as Record<string, unknown>[]} dataKey="date" h={260} withDots={days.length <= 31} curveType="linear" withGrid={false}
            series={[
              { name: 'chats', label: t('insights.overview.chats') },
              { name: 'new_people', label: t('insights.overview.newPeople') }
            ]}
            valueFormatter={(v) => formatCount(v)}
            labelFormatter={(v) => formatLocalDate(String(v), intlTag(locale), { day: 'numeric', month: 'short' })} />
        )}
      </Panel>

      <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="lg">
        <Panel title={t('insights.overview.heat')} note={t('insights.overview.heatNote')}>
          <HeatGrid grid={heat(rows, tz)} t={t} locale={locale} />
        </Panel>
        <Panel title={t('insights.overview.channels')}>
          {channels.length === 0 ? <Muted>{t('insights.empty.filtered')}</Muted> : (
            <>
              <DonutChart size={170} h={250} data={channels.map((c) => ({ name: channelLabel(t, c.name), value: c.count }))} valueFormatter={(v) => formatCount(v)} />
              <Stack gap={2}>
                {channels.map((c) => <Text key={c.name} role="caption" c={MUTED}>{channelLabel(t, c.name)}: {formatCount(c.count)}</Text>)}
              </Stack>
            </>
          )}
        </Panel>
      </SimpleGrid>

      {s && s.next_steps.length > 0 && (
        <Panel title={t('insights.overview.nextSteps')} note={<WholeReport t={t} />} testId="insights-next-steps">
          <ol className="rr-insights__steps">
            {s.next_steps.slice(0, 5).map((n, i) => (
              <li key={i}>
                <Text role="bodySmall" fw={600}>{n.title}</Text>
                <Text role="bodySmall" c={MUTED}>{n.detail}</Text>
                <SeeChats refs={n.refs} onSee={onSee} t={t} />
              </li>
            ))}
          </ol>
        </Panel>
      )}
    </Stack>
  );
}
